import React, { useEffect, useMemo, useRef, useState } from "react"; // NEW: useRef
import "./css/MonitoringDashboard.css";
import ScheduledJobsPanel from "./ScheduledJobsPanel";
import { apiFetch as fetch } from "apiClient";

const FRONTEND_BASE =
    process.env.REACT_APP_API_BASE ||
    window.location.origin; // fall back to current origin

const chip = (label, kind) => (
    <span className={`chip ${kind === "run" ? "chip--running" : kind === "fail" ? "chip--failed" : "chip--idle"}`}>
        {label}
    </span>
);

// helper: convert UTC string to local formatted string
const formatDateDisplay = (value) => {
    if (!value || typeof value !== "string") return value;

    // Match: 2025-12-20T13:27:11.447735 (optionally with shorter ms)
    const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2})(\.\d+)?$/.test(value);
    if (!match) {
        // format unknown → return as-is
        return value;
    }

    // Split into parts
    const [datePart, timePart] = value.split("T");
    const [year, month, day] = datePart.split("-").map(Number);
    const [hour, minute] = timePart.split(":").map(Number);

    // Create date without timezone shift (local interpretation)
    const d = new Date(year, month - 1, day, hour, minute);

    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    const formatted =
        `${day} ${monthNames[d.getMonth()]}, ${year} ` +
        `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;

    return formatted;
};


export default function MonitoringDashboard() {
    const [tasks, setTasks] = useState([]);
    const [asyncRuns, setAsyncRuns] = useState([]);
    const [schedStatus, setSchedStatus] = useState([]);
    const [schedRunning, setSchedRunning] = useState([]);
    const [schedRuns, setSchedRuns] = useState([]);
    const [selectedKind, setSelectedKind] = useState(null);
    const [selectedName, setSelectedName] = useState(null);
    const [activeTab, setActiveTab] = useState("status");
    const [isRunningNow, setIsRunningNow] = useState(false);

    // NEW: keep latest selection in a ref so SSE handler always sees current value
    const selectionRef = useRef({ kind: null, name: null });
    useEffect(() => {
        selectionRef.current = { kind: selectedKind, name: selectedName };
    }, [selectedKind, selectedName]);

    async function fetchAsyncStatus() {
        const r = await fetch("/api/monitoring/status", { credentials: "include" });
        setTasks(await r.json());
    }
    async function fetchScheduledStatus() {
        const r = await fetch("/api/monitoring/scheduled/status", { credentials: "include" });
        setSchedStatus(await r.json());
    }
    async function fetchScheduledRunning() {
        const r = await fetch("/api/monitoring/scheduled/running", { credentials: "include" });
        setSchedRunning(await r.json());
    }
    async function fetchAsyncRunning() {
        const r = await fetch("/api/monitoring/running", { credentials: "include" });
        setSchedRunning(await r.json());
    }
    async function fetchAsyncRuns(name) {
        const r = await fetch(`/api/monitoring/${encodeURIComponent(name)}/runs?limit=50`, { credentials: "include" });
        setAsyncRuns(await r.json());
    }
    async function fetchScheduledRuns(name) {
        const r = await fetch(`/api/monitoring/scheduled/${encodeURIComponent(name)}/runs?limit=50`, { credentials: "include" });
        setSchedRuns(await r.json());
    }
    async function triggerScheduled(name) {
        await fetch(`/api/monitoring/scheduled/${encodeURIComponent(name)}/trigger`, {
            method: "POST",
            credentials: "include"
        });
        // basic refresh
        fetchScheduledStatus();
        fetchScheduledRunning();
        fetchScheduledRuns(name);
    }

    // initial polling (10s)
    useEffect(() => {
        fetchAsyncStatus();
        fetchScheduledStatus();
        fetchScheduledRunning();
        const id = setInterval(() => {
            fetchAsyncStatus();
            fetchScheduledStatus();
            fetchScheduledRunning();
        }, 10000);
        return () => clearInterval(id);
    }, []);

    async function triggerAync(name) {
        await fetch(`/api/monitoring/${encodeURIComponent(name)}/trigger`, {
            method: "POST",
            credentials: "include"
        });
        // basic refresh
        fetchAsyncStatus();
        fetchAsyncRunning();
        fetchAsyncRuns(name);
    }

    // initial polling (10s)
    useEffect(() => {
        fetchAsyncStatus();
        fetchScheduledStatus();
        fetchScheduledRunning();
        const id = setInterval(() => {
            fetchAsyncStatus();
            fetchScheduledStatus();
            fetchScheduledRunning();
        }, 10000);
        return () => clearInterval(id);
    }, []);

    // NEW: SSE subscription – refresh when MonitoringTaskRerender arrives
    // NEW: SSE subscription – refresh when MonitoringTaskRerender arrives
    useEffect(() => {
        const es = new EventSource(`${FRONTEND_BASE}/events/stream`, {
            withCredentials: false
        });

        es.onopen = () => {
            console.log("[MonitoringDashboard] SSE opened /events/cnote");
        };

        es.addEventListener("connected", (e) => {
            console.log("[MonitoringDashboard] SSE 'connected' event:", e.data);
        });

        es.addEventListener("MonitoringTaskRerender", (e) => {
            console.log("[MonitoringDashboard] Received MonitoringTaskRerender:", e.data);

            // refresh main status lists
            fetchAsyncStatus();
            fetchScheduledStatus();
            fetchScheduledRunning();

            // also refresh runs for currently selected job, if any
            const { kind, name } = selectionRef.current;
            if (name) {
                if (kind === "async") {
                    fetchAsyncRuns(name);
                } else if (kind === "scheduled") {
                    fetchScheduledRuns(name);
                }
            }
        });

        es.onerror = (err) => {
            // IMPORTANT: do NOT close here; let the browser auto-reconnect
            console.error("[MonitoringDashboard] SSE error on /events/cnote:", err);
            // If you want to detect permanent failures, you can inspect es.readyState here.
            // But don't call es.close() – that prevents auto-reconnect and you'll miss events.
        };

        return () => {
            console.log("[MonitoringDashboard] Cleaning up SSE /events/cnote");
            es.close();
        };
    }, []); // open SSE only once

    // load runs when selection changes
    useEffect(() => {
        if (!selectedName) return;
        if (selectedKind === "async") fetchAsyncRuns(selectedName);
        if (selectedKind === "scheduled") fetchScheduledRuns(selectedName);
    }, [selectedKind, selectedName]);

    const nowRunningAsync = useMemo(
        () => tasks.filter(t => t.taskType === "ASYNC" && t.status === "RUNNING"),
        [tasks]
    );

    const asyncTasks = useMemo(
        () => tasks.filter(t => t.taskType === "ASYNC"),
        [tasks]
    );

    const scheduledTasks = useMemo(
        () => tasks.filter(t => t.taskType === "SCHEDULED"),
        [tasks]
    );

    // Handle Run now click
    async function handleRunNow() {
        if (!selectedName || isRunningNow) return;  // 👈 prevent double-trigger
        setIsRunningNow(true);

        if (selectedKind === "scheduled") {
            await triggerScheduled(selectedName);
        } else {
            await triggerAync(selectedName);
        }
    }

    // Keep isRunningNow in sync with backend "running" list
    // Keep isRunningNow in sync with backend "running" status
    useEffect(() => {
        if (!selectedName) return;

        let stillRunning = false;

        if (selectedKind === "scheduled") {
            // DB status for scheduled jobs
            stillRunning = schedRunning.some(
                (j) => j.jobName === selectedName
            );
        } else if (selectedKind === "async") {
            // DB status for async monitors (from /api/monitoring/status)
            stillRunning = tasks.some(
                (t) =>
                    t.taskType === "ASYNC" &&
                    t.taskName === selectedName &&
                    t.status === "RUNNING"
            );
        }

        if (!stillRunning && isRunningNow) {
            // It *was* running in UI but DB says it's not anymore
            setIsRunningNow(false);

            // refresh history to show the finished run
            if (selectedKind === "scheduled") {
                fetchScheduledRuns(selectedName);
            } else if (selectedKind === "async") {
                fetchAsyncRuns(selectedName);
            }
        }

        if (stillRunning && !isRunningNow) {
            // DB says this job is already RUNNING (even if we didn't click Run now)
            setIsRunningNow(true);
        }
    }, [
        selectedName,
        selectedKind,
        schedRunning,
        tasks,
        isRunningNow,
    ]);


    // Build the list of rows to display in the history table
    const displayRuns = useMemo(() => {
        if (!selectedName) return [];

        if (selectedKind === "async") {
            return asyncRuns; // unchanged for async
        }

        const base = schedRuns || [];

        // running jobs for this scheduled task (from backend)
        const runningForJob = schedRunning.filter(
            (j) => j.jobName === selectedName
        );

        let runningRows = runningForJob.map((j) => {
            const startAt = j.startAt || j.appLastRunAt || new Date().toISOString();
            return {
                id: `live-${j.jobName}-${startAt}`,
                startAt,
                endAt: null,
                status: "RUNNING",
                errorMessage: null
            };
        });

        // If we CLICKED Run now but backend hasn't populated /running yet,
        // still show an optimistic RUNNING row for UI feedback.
        if (isRunningNow && runningRows.length === 0) {
            const startAt = new Date().toISOString();
            runningRows.push({
                id: `live-${selectedName}-${startAt}`,
                startAt,
                endAt: null,
                status: "RUNNING",
                errorMessage: null
            });
        }

        // avoid duplicates if backend also returns a RUNNING row in history
        const seen = new Set(base.map((r) => `${r.status}-${r.startAt}`));
        const merged = [
            ...runningRows.filter((r) => !seen.has(`${r.status}-${r.startAt}`)),
            ...base
        ];

        return merged;
    }, [selectedKind, selectedName, asyncRuns, schedRuns, schedRunning, isRunningNow]);

    // Helper: get display name for a given selected job/monitor
    const getDisplayName = (name, kind) => {
        if (!name) return "";
        const task = tasks.find(t => t.taskName === name);
        return task?.displayName || name;
    };

    return (
        <div className="p-4 gap-6 monitoring-root">
            {/*<h2 className="h2">Monitoring</h2>*/}

            <div className="tabs">
                <button
                    className={`tab ${activeTab === "status" ? "tab--active" : ""}`}
                    onClick={() => setActiveTab("status")}
                >
                    Job Status
                </button>
                <button
                    className={`tab ${activeTab === "history" ? "tab--active" : ""}`}
                    onClick={() => setActiveTab("history")}
                >
                    Run History
                </button>
            </div>

            {activeTab === "status" && (
                <>
                    <div className="card">
                        <div className="row mb-4">
                            <div className="h3">Currently running Async Jobs</div>
                        </div>
                        <div className="now-row">
                            {nowRunningAsync.map((t) => (
                                <span
                                    key={t.taskName}
                                    className="now-chip"
                                    title="Async monitor"
                                >
                                    {chip(t.displayName || t.taskName, "run")}
                                </span>
                            ))}
                            {nowRunningAsync.length === 0 && (
                                <span className="subtle">Nothing running</span>
                            )}
                        </div>
                    </div>

                    <ScheduledJobsPanel />
                </>
            )}

            {activeTab === "history" && (
                <div className="two-col">
                    <aside className="left-rail card">
                        <div className="h3 mb-3">Select to view history</div>
                        <div className="rail-section">
                            <div className="rail-title">Async monitors</div>
                            <ul className="rail-list">
                                {asyncTasks.map((t) => (
                                    <li key={t.taskName}>
                                        <button
                                            className={`rail-item ${
                                                selectedKind === "async" &&
                                                selectedName === t.taskName
                                                    ? "rail-item--active"
                                                    : ""
                                            }`}
                                            onClick={() => {
                                                setSelectedKind("async");
                                                setSelectedName(t.taskName);
                                            }}
                                        >
                                            <span className="rail-name">
                                                {t.displayName || t.taskName}
                                            </span>
                                            <span
                                                className={`rail-badge ${
                                                    t.status === "RUNNING"
                                                        ? "run"
                                                        : t.status === "FAILED"
                                                            ? "fail"
                                                            : "ok"
                                                }`}
                                            >
                                                {t.status}
                                            </span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="rail-section">
                            <div className="rail-title">Scheduled jobs</div>
                            <ul className="rail-list">
                                {scheduledTasks.map((t) => {
                                    const schedRow =
                                        schedStatus.find(
                                            (x) => x.jobName === t.taskName
                                        ) || null;
                                    const ranToday = schedRow ? schedRow.ranTodayByApp : false;
                                    const displayLabel =
                                        (schedRow && schedRow.displayName) ||
                                        t.displayName ||
                                        t.taskName;

                                    return (
                                        <li key={t.taskName}>
                                            <button
                                                className={`rail-item ${
                                                    selectedKind === "scheduled" &&
                                                    selectedName === t.taskName
                                                        ? "rail-item--active"
                                                        : ""
                                                }`}
                                                onClick={() => {
                                                    setSelectedKind("scheduled");
                                                    setSelectedName(t.taskName);
                                                }}
                                            >
                                                <span className="rail-name">
                                                    {displayLabel}
                                                </span>
                                                <span
                                                    className={`rail-badge ${
                                                        ranToday ? "ok" : "warn"
                                                    }`}
                                                >
                                                    {ranToday ? "Ran" : "Not yet"}
                                                </span>
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </aside>

                    <section className="card main-panel">
                        {!selectedName ? (
                            <div className="subtle">
                                Select an async monitor or scheduled job to view its
                                recent runs.
                            </div>
                        ) : (
                            <>
                                <div className="run-header">
                                    <h3 className="h3">
                                        Recent runs —{" "}
                                        {getDisplayName(selectedName, selectedKind)}
                                    </h3>
                                    <button
                                        className={`btn btn--primary run-now-btn ${
                                            isRunningNow
                                                ? "run-now-btn--busy"
                                                : ""
                                        }`}
                                        disabled={isRunningNow}
                                        onClick={handleRunNow}
                                    >
                                        {isRunningNow ? "Running..." : "Run now"}
                                    </button>
                                </div>

                                <div className="table-wrap">
                                    <table className="table table--zebra table--sticky table--runs">
                                        <thead>
                                        <tr>
                                            <th>Start (local)</th>
                                            <th>End (local)</th>
                                            <th>Duration</th>
                                            <th>Status</th>
                                            <th>Message</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {displayRuns.length === 0 ? (
                                            <tr>
                                                <td colSpan={5}>No runs yet.</td>
                                            </tr>
                                        ) : (
                                            displayRuns.map((r) => {
                                                const startStr =
                                                    formatDateDisplay(r.startAt);
                                                const endStr = r.endAt
                                                    ? formatDateDisplay(r.endAt)
                                                    : "-";
                                                const start = new Date(r.startAt);
                                                const end = r.endAt
                                                    ? new Date(r.endAt)
                                                    : null;
                                                const durMs = end
                                                    ? end.getTime() -
                                                    start.getTime()
                                                    : Date.now() -
                                                    start.getTime();
                                                const durStr = `${Math.floor(
                                                    durMs / 60000
                                                )}m ${Math.floor(
                                                    durMs / 1000
                                                ) % 60}s`;

                                                let detail = null;
                                                if (r.status === "FAILED" || r.status === "ABORTED") {
                                                    detail = r.errorMessage || null;
                                                } else if (r.status === "SUCCESS") {
                                                    detail = r.successMessage || null;
                                                } else {
                                                    detail = null; // RUNNING, etc.
                                                }

                                                return (
                                                    <tr key={r.id}>
                                                        <td className="mono">{startStr}</td>
                                                        <td className="mono">{endStr}</td>
                                                        <td className="mono">{durStr}</td>
                                                        <td>
                                                            <span
                                                                className={`chip ${
                                                                    r.status === "RUNNING"
                                                                        ? "chip--running"
                                                                        : (r.status === "FAILED" || r.status === "ABORTED")
                                                                            ? "chip--failed"
                                                                            : "chip--idle"
                                                                }`}
                                                            >
                                                                {r.status}
                                                            </span>
                                                        </td>
                                                        <td
                                                            className={
                                                                (r.status === "FAILED" || r.status === "ABORTED")
                                                                    ? "run-detail-error"
                                                                    : r.status === "SUCCESS"
                                                                        ? "run-detail-success"
                                                                        : "run-detail-neutral"
                                                            }
                                                        >
                                                            {detail ? detail.slice(0, 300) : ""}
                                                        </td>

                                                    </tr>
                                                );
                                            })
                                        )}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                    </section>
                </div>
            )}
        </div>
    );
}
